import { useEffect, useRef, useState } from 'react';

const services = [
  {
    id: 1,
    icon: 'ri-heart-line',
    title: 'Bridal Flowers',
    subtitle: 'ブライダルフラワー',
    description: 'ウェディングブーケからセレモニーフラワー、テーブルフラワーまで。花嫁様の夢を叶える、世界にひとつだけのウェディングフラワーをトータルプロデュースします。',
    tags: ['ブーケ', 'コサージュ', '会場装花', 'セレモニーフラワー'],
    image: 'https://public.readdy.ai/ai/img_res/8a8a3ebb7a8a93a91dcd68a023630762.jpg',
  },
  {
    id: 2,
    icon: 'ri-gift-line',
    title: 'Flower Gift',
    subtitle: 'フラワーギフト',
    description: '誕生日・記念日・お祝いなど、大切な方への贈り物に。季節の花を組み合わせた花束やアレンジメントを、想いを込めて制作します。当日配送も承ります。',
    tags: ['花束', 'アレンジメント', '記念日', 'プロポーズ'],
    image: 'https://readdy.ai/api/search-image?query=Luxurious%20wrapped%20flower%20bouquet%20with%20pastel%20pink%20and%20peach%20roses%2C%20white%20ranunculus%2C%20soft%20green%20leaves%20tied%20with%20satin%20ribbon%2C%20elegant%20gift%20wrapping%20in%20kraft%20paper%20with%20dried%20flowers%20accent%2C%20romantic%20present%20on%20cream%20background%2C%20artisan%20florist%20craft%2C%20warm%20golden%20hour%20light&width=600&height=700&seq=bloom-service-gift-1&orientation=portrait',
  },
  {
    id: 3,
    icon: 'ri-home-heart-line',
    title: 'Interior Flowers',
    subtitle: 'インテリアフラワー',
    description: '空間に花のある豊かな暮らしをご提案。ご自宅・オフィス・店舗などの空間に合わせた定期フラワーサービスやプリザーブドフラワーでインテリアを彩ります。',
    tags: ['定期便', 'プリザーブド', 'ドライフラワー', 'オフィス'],
    image: 'https://readdy.ai/api/search-image?query=Beautiful%20interior%20floral%20arrangement%20in%20elegant%20vase%20on%20minimalist%20Scandinavian%20shelf%20with%20white%20wall%20background%2C%20mix%20of%20dried%20pampas%20grass%20neutral%20tones%20beige%20and%20cream%20dried%20botanicals%2C%20ambient%20warm%20home%20lighting%20creating%20cozy%20atmosphere%2C%20lifestyle%20interior%20photography%2C%20natural%20textures%20and%20earthy%20tones&width=600&height=700&seq=bloom-service-interior-1&orientation=portrait',
  },
  {
    id: 4,
    icon: 'ri-plant-line',
    title: 'Flower Lesson',
    subtitle: 'フラワーレッスン',
    description: '初心者から上級者まで、少人数制のプライベートレッスンを開講。季節の花を使ったアレンジメントや、ウェディングブーケ制作などのコースをご用意しています。',
    tags: ['初心者OK', 'プライベート', '少人数', '資格取得'],
    image: 'https://readdy.ai/api/search-image?query=Flower%20arrangement%20lesson%20workshop%20scene%2C%20hands%20carefully%20placing%20fresh%20flowers%20in%20a%20vase%2C%20close%20up%20of%20florist%20student%20learning%20with%20beautiful%20soft%20pink%20roses%20and%20foliage%20on%20white%20wooden%20table%2C%20small%20intimate%20class%20setting%20with%20natural%20light%2C%20elegant%20minimal%20studio%20space%2C%20hands-on%20learning%20experience&width=600&height=700&seq=bloom-service-lesson-1&orientation=portrait',
  },
];

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={ref}
      className="py-24 lg:py-36 px-6 lg:px-12 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-noto text-xs tracking-[0.4em] text-[#c4956a] uppercase mb-4">Services</p>
          <h2 className="font-cormorant text-5xl lg:text-6xl text-[#2c2218] font-light">私たちのサービス</h2>
        </div>

        {/* Service cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <div
              key={service.id}
              className={`group bg-[#faf8f5] rounded-lg overflow-hidden transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100 + 200}ms` }}
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.subtitle}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Content */}
              <div className="p-6">
                <div className="w-8 h-8 flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-[#c4956a] text-xl`}></i>
                </div>
                <h3 className="font-cormorant text-xl text-[#2c2218] font-medium mb-1">{service.title}</h3>
                <p className="font-noto text-xs text-[#c4956a] tracking-wider mb-4">{service.subtitle}</p>
                <p className="font-noto text-xs text-[#6b5c4e] leading-relaxed mb-4">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.tags.map(tag => (
                    <span key={tag} className="font-noto text-xs text-[#8b7355] bg-[#f0e8de] px-2 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
